/**
 * 层定义与 overlay 聚合纯函数（可单测）
 * 层顺序：大限(绿) → 流年(蓝) → 流月(橙) → 流日(紫)，圆角正方形色块+白色字
 */
import type { RenshiMap, SiHuaMap, Zhi } from '../types/base';
import type { DaxianOverlay, LiunianOverlay, LiuriOverlay, LiushiOverlay, LiuyueMonth } from '../types/overlay';

/** 层 key（叠层顺序即数组顺序；xiaoxian 是流年附属层，叠在流年之后、流月之前） */
export type LayerKey = 'daxian' | 'liunian' | 'xiaoxian' | 'liuyue' | 'liuri' | 'liushi';

/** 层切换 tab（含本命） */
export type LayerTab = LayerKey | 'benming';

/** 层展示元信息（Tailwind class，用于宫格标签着色） */
export interface LayerMeta {
  key: LayerKey;
  label: string;
  textClass: string;
  badgeClass: string;
  barClass: string;
}

export const LAYER_META: LayerMeta[] = [
  {
    key: 'daxian',
    label: '大限',
    textClass: 'text-green-700',
    badgeClass: 'bg-green-600',
    barClass: 'bg-green-600',
  },
  {
    key: 'liunian',
    label: '流年',
    textClass: 'text-blue-700',
    badgeClass: 'bg-blue-600',
    barClass: 'bg-blue-600',
  },
  {
    key: 'liuyue',
    label: '流月',
    textClass: 'text-amber-700',
    badgeClass: 'bg-amber-600',
    barClass: 'bg-amber-600',
  },
  {
    key: 'liuri',
    label: '流日',
    textClass: 'text-purple-700',
    badgeClass: 'bg-purple-600',
    barClass: 'bg-purple-600',
  },
  {
    key: 'liushi',
    label: '流时',
    textClass: 'text-slate-800',
    badgeClass: 'bg-slate-700',
    barClass: 'bg-slate-700',
  },
  {
    // 小限：流年附属层（文字 teal-300 淡青），只在选中流年后叠加
    key: 'xiaoxian',
    label: '小限',
    textClass: 'text-teal-300',
    badgeClass: 'bg-teal-400',
    barClass: 'bg-teal-400',
  },
];

/** 十二时辰标签 */
export const TIME_LABELS = ['子时','丑时','寅时','卯时','辰时','巳时','午时','未时','申时','酉时','戌时','亥时'];

/** 层缩写前缀字（人事宫两字缩写用：大兄/年夫/小命…，对齐旧排盘图二） */
export const LAYER_CHAR: Record<LayerKey, string> = {
  daxian: '大',
  liunian: '年',
  xiaoxian: '小',
  liuyue: '月',
  liuri: '日',
  liushi: '时',
};

/** 当前盘叠加的所有层（按叠层顺序返回；含层描述信息） */
export interface ActiveLayer {
  meta: LayerMeta;
  title: string;
  subtitle: string;
  sihua: SiHuaMap;
  renshigong: RenshiMap;
}

/** 当前盘全部可用层数据（四层 pack 快照，流日盘才有四层） */
export interface LayerPacks {
  daxian?: DaxianOverlay;
  liunian?: LiunianOverlay;
  /** 流月盘：当前选中月；流日盘：单元素 */
  liuyue?: LiuyueMonth;
  liuri?: LiuriOverlay;
  liushi?: LiushiOverlay;
}

/** 最大显示层数（由传入的 layer 决定：大限盘=1 … 流日盘=4） */
export function activeLayers(layer: LayerKey, packs: LayerPacks): ActiveLayer[] {
  const out: ActiveLayer[] = [];
  const push = (meta: LayerMeta, title: string, subtitle: string, sihua: SiHuaMap, renshigong: RenshiMap) => {
    out.push({ meta, title, subtitle, sihua, renshigong });
  };

  if (packs.daxian) {
    const d = packs.daxian;
    push(
      LAYER_META[0],
      d.tongxian ? `童限 虚岁${d.xvsui}` : `第${d.order}大限`,
      d.yearRange,
      d.sihua,
      d.renshigong,
    );
  }
  if (layer === 'liunian' || layer === 'liuyue' || layer === 'liuri') {
    if (packs.liunian) {
      const n = packs.liunian;
      push(LAYER_META[1], `${n.year}流年`, `${n.gan}${n.zhi}`, n.sihua, n.renshigong);
    }
  }
  // 小限：流年附属层——只在选中流年（含流月/流日/流时下钻）时叠加；
  // 纯大限/本命盘不显示（即使之前选过流年、packs 里有流年数据）
  if (layer !== 'daxian' && packs.liunian?.xiaoxian) {
    const x = packs.liunian.xiaoxian;
    push(LAYER_META[5], `小限 虚岁${x.xvsui}`, `${x.gan}${x.zhi}`, {}, x.renshigong);
  }
  if (layer === 'liuyue' || layer === 'liuri') {
    if (packs.liuyue) {
      const m = packs.liuyue;
      push(LAYER_META[2], `流月${m.month}`, `${m.gan}${m.zhi}`, m.sihua, m.renshigong);
    }
  }
  if (layer === 'liuri' && packs.liuri) {
    const r = packs.liuri;
    push(LAYER_META[3], `流日 ${r.month}/${r.day}`, `${r.gan}${r.zhi}日`, r.sihua, r.renshigong);
  }
  if (packs.liushi) {
    const h = packs.liushi;
    push(LAYER_META[4], `流时 ${TIME_LABELS[h.shiIndex]}`, `${h.gan}${h.zhi}`, h.sihua, h.renshigong);
  }
  return out;
}

/** 单宫叠加的四化标签列表（该宫在本层有化落点时才有） */
export interface SihuaTag {
  layer: LayerMeta;
  /** 化禄/化权/化科/化忌 */
  kind: keyof SiHuaMap;
  /** "星 地支" 原始值 */
  raw: string;
  /** 星名 */
  star: string;
}

const ZHI_LIST = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;

/** 解析 "星 地支"/"星 离心自化" → 落宫地支或自化标记 */
export function parseSihua(raw: string): { star: string; zhi: Zhi | null; self: 'centrifugal' | 'centripetal' | null } {
  const selfMatch = raw.match(/^(.+?)\s+(离心自化|向心自化)$/);
  if (selfMatch) {
    return { star: selfMatch[1], zhi: null, self: selfMatch[2] === '离心自化' ? 'centrifugal' : 'centripetal' };
  }
  const parts = raw.split(/\s+/);
  const zhi = parts[parts.length - 1];
  if (ZHI_LIST.includes(zhi as (typeof ZHI_LIST)[number])) {
    return { star: parts.slice(0, -1).join(' '), zhi: zhi as Zhi, self: null };
  }
  return { star: raw, zhi: null, self: null };
}

/** 聚合某宫的所有层四化标签（按叠层顺序） */
export function sihuaTagsForPalace(layer: LayerKey, packs: LayerPacks, zhi: Zhi): SihuaTag[] {
  const tags: SihuaTag[] = [];
  for (const active of activeLayers(layer, packs)) {
    for (const [kind, raw] of Object.entries(active.sihua) as [keyof SiHuaMap, string][]) {
      const parsed = parseSihua(raw);
      if (parsed.zhi === zhi) {
        tags.push({ layer: active.meta, kind, raw, star: parsed.star });
      }
    }
  }
  return tags;
}

/** 该宫在当前叠加层中的"最新人事宫名"（最上层覆盖；无覆盖返回本命人事宫名） */
export function topRenshiName(layer: LayerKey, packs: LayerPacks, zhi: Zhi, benmingName: string): {
  name: string;
  layerMeta: LayerMeta | null;
} {
  const acts = activeLayers(layer, packs);
  for (let i = acts.length - 1; i >= 0; i--) {
    const name = acts[i].renshigong[zhi];
    if (name) return { name, layerMeta: acts[i].meta };
  }
  return { name: benmingName, layerMeta: null };
}
